import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Star, ShieldCheck } from 'lucide-react';
import { content } from '../data/content';
import { SunsetBands } from './SunsetBands';
export function Hero() {
  const ref = useRef(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  return <section id="home" ref={ref} className="relative h-[90vh] min-h-[600px] flex items-center overflow-hidden" data-id="element-140">
      {/* Parallax Background */}
      <motion.div style={{
      y
    }} className="absolute inset-0 w-full h-full" data-id="element-141">
        <img src="https://cdn.magicpatterns.com/uploads/4wnn3H41EeSWsS9aScMYTn/502984336_17909468319148706_1810881730810036588_n.jpg" alt="Beautifully painted modern Arizona home exterior" className="w-full h-full object-cover" data-id="element-142" />
        {/* Mobile / small tablet (below 768px): even full-width vertical overlay */}
        <div className="absolute inset-0 md:hidden" style={{
        background: 'linear-gradient(to bottom, rgba(26,14,10,0.85) 0%, rgba(26,14,10,0.7) 50%, rgba(26,14,10,0.88) 100%)'
      }} data-id="element-143" />
        {/* Desktop (768px+): left-to-right gradient, dark left, natural photo on right */}
        <div className="absolute inset-0 hidden md:block" style={{
        background: 'linear-gradient(to right, rgba(26,14,10,0.94) 0%, rgba(26,14,10,0.82) 35%, rgba(26,14,10,0.45) 65%, rgba(26,14,10,0.12) 100%)'
      }} data-id="element-144" />
        {/* Desktop (768px+): bottom-left vertical darkening behind the trust line */}
        <div className="absolute inset-0 hidden md:block" style={{
        background: 'linear-gradient(to top, rgba(26,14,10,0.6) 0%, rgba(26,14,10,0) 40%)'
      }} data-id="element-145" />
      </motion.div>

      {/* Content */}
      <motion.div style={{
      opacity
    }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-20" data-id="element-146">
        <div className="max-w-3xl mx-auto md:mx-0 text-center md:text-left" data-id="element-147">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} style={{
          textShadow: '0 2px 16px rgba(0,0,0,0.55)'
        }} className="font-display text-4xl md:text-6xl lg:text-7xl font-bold text-brand-cream leading-tight mb-6" data-id="element-148">
            {content.hero.headline}
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} style={{
          textShadow: '0 1px 8px rgba(0,0,0,0.6)'
        }} className="text-lg md:text-xl text-brand-cream/90 mb-10 max-w-2xl mx-auto md:mx-0 leading-relaxed font-light" data-id="element-149">
            {content.hero.subheadline}
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="flex flex-col sm:flex-row gap-4 mb-12 justify-center md:justify-start" data-id="element-150">
            <a href="#contact" className="inline-flex justify-center items-center px-8 py-4 text-lg font-medium rounded bg-brand-orange text-brand-cream hover:bg-opacity-90 transition-all shadow-lg min-h-[48px]" data-id="element-151">
              {content.hero.primaryCTA}
            </a>
            <a href="#services" className="inline-flex justify-center items-center px-8 py-4 text-lg font-medium rounded border-2 border-brand-cream text-brand-cream hover:bg-brand-cream hover:text-brand-brown transition-all min-h-[48px]" data-id="element-152">
              {content.hero.secondaryCTA}
            </a>
          </motion.div>

          {/* Trust Strip */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 1,
          delay: 1
        }} style={{
          textShadow: '0 1px 8px rgba(0,0,0,0.6)'
        }} className="flex flex-col sm:flex-row items-center justify-center md:items-start md:justify-start gap-4 sm:gap-8 text-brand-cream text-sm font-medium" data-id="element-153">
            <div className="flex items-center" data-id="element-154">
              <ShieldCheck className="w-5 h-5 mr-2 text-brand-amber" data-id="element-155" />
              {content.hero.trustStrip}
            </div>
            <div className="flex items-center" data-id="element-156">
              <div className="flex mr-2" data-id="element-157">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-brand-amber text-brand-amber" data-id="element-158" />)}
              </div>
              5.0 Google Rating
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Bottom Sunset Bleed */}
      <div className="absolute bottom-0 left-0 w-full z-20" data-id="element-159">
        <SunsetBands heights={['h-2', 'h-3', 'h-4', 'h-6']} data-id="element-160" />
      </div>
    </section>;
}